import React, { useCallback, useRef } from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useCardSize } from "components/Report-generator/context/CardSizeContext";
import { useFormatNumericHook } from "hooks";
import type { Chart } from "store/types";

// Match the Snapshot chart palette (src/components/reports/Chart/useChartHook.ts `graphColors`).
const COLORS = [
  "#4690F5",
  "#32CED8",
  "#D428BD",
  "#496682",
  "#6E45EB",
  "#EBAF01",
  "#D93E3E",
  "#D77108",
  "#0ABB91",
  "#FEA458"
];
const truncate = (s: string, max: number) => (s.length > max ? s.slice(0, max - 1) + "\u2026" : s);

interface Props {
  data?: Chart;
  loading?: boolean;
  animation?: boolean;
}

export const ChartCard: React.FC<Props> = ({ data, loading, animation = true }) => {
  const { isEditMode, widthPercent } = useCardSize();
  const formatNumeric = useFormatNumericHook();
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      observerRef.current = new ResizeObserver(() => {
        chartRef.current?.chart?.reflow();
      });
      observerRef.current.observe(node);
    }
  }, []);

  if (loading || !data) {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  const chartData = data.data;
  const chartType = chartData.type || "pie";
  const isPie = chartType === "pie";

  const resolvedType =
    chartType === "barStacked" || chartType === "clusterBar" || chartType === "top5"
      ? "bar"
      : chartType === "columnStacked"
        ? "column"
        : chartType;

  const isScatter = chartType === "scatter";

  const getSeries = (): Highcharts.SeriesOptionsType[] => {
    if (isPie) {
      return [{ ...chartData, type: "pie" } as Highcharts.SeriesOptionsType];
    }
    if (isScatter) {
      return chartData.data as any;
    }
    if (chartType === "waterfall") {
      return [
        {
          data: ((chartData.data as any)[0] as any)?.data?.map((item: any, index: number) => ({
            ...item,
            color: COLORS[index % COLORS.length]
          }))
        } as Highcharts.SeriesOptionsType
      ];
    }
    if (chartType === "clusterBar") {
      return [
        {
          name: data.axis,
          type: "bar",
          data: (chartData.data as any[]).map((item) => {
            const y = item.data?.[0]?.y ?? 0;
            return { name: item.name, y, color: y > 0 ? "#066471" : "#D46535" };
          }),
          showInLegend: false
        } as Highcharts.SeriesOptionsType
      ];
    }
    return [...((chartData.data as any) || [])];
  };

  const titleParts = data.title?.split("-") || [];
  const valuePrefix = data.postfix === "%" ? "" : data.postfix;
  const valuePostfix = data.postfix === "%" ? data.postfix : "";
  const isStacked = chartType === "barStacked" || chartType === "columnStacked";

  // Run the (now high-precision) value through the global number format
  // (decimals + abbreviation), keeping the unit prefix/postfix.
  const formatValue = (value: number | undefined): string =>
    `${valuePrefix}${formatNumeric({ value: value ?? 0, format: {} })}${valuePostfix}`;

  // Tooltips mirror the Snapshot chart (series name + color swatch, percentage for
  // pie/stacked), but the value respects the global number format instead of a fixed
  // 2-decimal format.
  const tooltip: Highcharts.TooltipOptions = {
    useHTML: true,
    formatter(this: Highcharts.TooltipFormatterContextObject): string {
      const value = formatValue(this.y ?? undefined);
      const percentage = (this.percentage ?? 0).toFixed(2);
      if (isScatter) {
        const xValue = `${data.postfix}${formatNumeric({ value: this.x ?? 0, format: {} })}`;
        return (
          `<span>${titleParts[0]?.trim()}: <b>${xValue}</b></span>` +
          `<br><span>${titleParts[1]?.trim()}: <b>${value}</b></span>`
        );
      }
      if (isPie) {
        return `<span>${data.axis}: <b>${value} (${percentage}%)</b></span>`;
      }
      if (isStacked) {
        return `<span style="color:${this.color}">${this.series.name}</span> <br>${data.axis}: <b>${value} (${percentage}%)</b>`;
      }
      return `<span style="color:${this.color}">${this.series.name}</span> <br>${data.axis}: <b>${value}</b>`;
    }
  };

  const options: Highcharts.Options = {
    chart: {
      type: resolvedType,
      height: null,
      marginTop: 8,
      style: { fontFamily: "inherit" },
      ...(isScatter && { zoomType: "xy" as const }),
      ...(!animation && { animation: false })
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    tooltip,
    plotOptions: {
      series: !animation ? { animation: false } : undefined,
      pie: {
        innerSize: chartData.innerSize || "45%",
        dataLabels: {
          enabled: true,
          distance: 4,
          format: "{point.percentage:.1f} %",
          style: {
            fontSize: widthPercent <= 34 ? "8px" : widthPercent <= 50 ? "9px" : "11px",
            fontWeight: "normal",
            textOutline: "none"
          }
        },
        showInLegend: true
      },
      column: { stacking: chartType === "columnStacked" ? "normal" : undefined },
      bar: { stacking: chartType === "barStacked" ? "normal" : undefined },
      ...(isScatter && {
        scatter: {
          marker: {
            radius: 6.5,
            symbol: "circle"
          }
        }
      })
    },
    xAxis: isScatter
      ? { title: { text: titleParts[0]?.trim() } }
      : { categories: [""], crosshair: true },
    yAxis: isScatter
      ? { min: 0, title: { text: titleParts[1]?.trim() } }
      : { title: { text: data.axis || null }, gridLineColor: "#F3F4F6" },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      itemStyle: {
        fontSize: widthPercent <= 34 ? "8px" : widthPercent <= 50 ? "9px" : "11px",
        fontWeight: "normal",
        color: "#374151"
      },
      symbolRadius: 4,
      margin: 8,
      itemDistance: widthPercent <= 34 ? 6 : 12,
      labelFormatter() {
        const maxLen = widthPercent <= 34 ? 10 : 14;
        return truncate(this.name, maxLen);
      }
    },
    colors: COLORS,
    series: getSeries()
  };

  return (
    <Box ref={containerRef} sx={{ p: "16px 8px 8px", height: "100%", display: "flex", flexDirection: "column" }}>
      {loading && <LinearProgress sx={{ mb: "4px" }} />}
      {data.title && (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            pl: "12px",
            mb: "4px"
          }}
        >
          {data.title}
        </Typography>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <HighchartsReact ref={chartRef} key={chartType} highcharts={Highcharts} options={options} containerProps={{ style: { height: "100%" } }} />
      </Box>
    </Box>
  );
};
